import type { Building } from "building-types/building/buildingTypes";
import { DIM_EXT } from "building-types/constants/dimensionConstants";
import type { DimensionGeometryProps, GeneratedDimensionMap } from "building-types/dimensions/dimensionTypes";
import type { Doc } from "building-types/doc";
import {
  autoDimensionFloorPlanInteriorByOrthogonalRoomEdgeHandleNew,
  autoDimensionFloorPlanInteriorByRoomEdgePairHandleNew,
} from "building-types/handles/autoDimensionHandleTools";
import type { RoomEdge } from "building-types/handles/docEntityHandleTypes";
import type { EdgeData } from "building-types/provenance/halfEdgeDataTypes";
import {
  AutoInteriorDimensionProvenanceCriteria,
  makeProvenanceCriteria,
} from "building-types/provenance/provenanceCriteriaTools";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import { InteriorDimensionType } from "building-types/settings/settingsTypesEnum";
import { multimapAdd } from "common/data/multimap";
import { getDimLabelPosition } from "doc/dimensions";
import { isCompositeLevelContentSourceId } from "doc/docViewport";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import type { VisitedRoomsTable } from "doc/plans/interiorLabelObstacle";
import { calculateRoomSetArea } from "doc/plans/interiorLabelObstacle";
import {
  collectDefinedHalfEdges,
  edgeDataUnique,
  halfEdgeDataNew,
  polyLine2EdgeDataNewFromHalfEdges,
} from "doc/provenance/halfEdgeDataTools";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import {
  generatedInteriorDimensionNew,
  getRoomEdgesFromHalfEdgeData,
  getRoomEdgesFromHalfEdges,
} from "doc/utils/dimensionTools";
import { aabb2AddPoint, aabb2Grow, aabb2IsValid, aabb2Union } from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import { line2Normal } from "geom/line2";
import { oppositeHalfEdgeIndex } from "geom/polyclip/dcelFromPolygons";
import { polyLine2AABB2, polyLine2Copy, polyLine2EdgeEvalOffset, polyLine2Move } from "geom/polyLine2";
import { buildingIsEmpty } from "../building/buildingIsEmpty";
import { buildCompositeRoomSetWallIndexContext } from "./buildCompositeRoomSetWallIndexContext";
import { collectCompositeInteriorDimensionRoomGroups } from "./collectCompositeInteriorDimensionRoomGroups";
import { addInteriorDimensionsByRoomGroup } from "./interiorDimensionsByRoomGroup";
import { getInteriorDimensionStripsByString } from "./interiorDimensionsByString";

interface MakeInteriorFloorPlanDimensionsByLevelProps {
  readonly building: Pick<
    Building,
    | "planarGraphs"
    | "rooms"
    | "roomLevelIndices"
    | "roomsByLevel"
    | "settings"
    | "walls"
    | "dimensionBehavior"
    | "wallMasses"
    | "mirror"
    | "levels"
  > & { doc: Pick<Doc, "compositeLevels" | "levels"> };
  levelId: string;
}

export function makeInteriorFloorPlanDimensionsByLevel({
  building,
  levelId,
}: MakeInteriorFloorPlanDimensionsByLevelProps): GeneratedDimensionMap {
  if (buildingIsEmpty(building)) {
    return {};
  }
  const interiorFloorPlanDim = generateInteriorFloorPlanDimensionsByLevel({ building, levelId });

  // convert to generated dimension

  return generatedInteriorDimensionHydrateDocEntityHandle(interiorFloorPlanDim, building, levelId);
}

export function generateInteriorFloorPlanDimensionsByLevel({
  building,
  levelId,
}: MakeInteriorFloorPlanDimensionsByLevelProps): DimensionGeometryProps[] {
  const { interiorDimensionType, interiorDimensionAreaThreshold } = building.settings.planset;
  const dimExt = DIM_EXT;
  const dims: DimensionGeometryProps[] = [];
  const isCompositeLevel = isCompositeLevelContentSourceId(building.doc, levelId);

  if (interiorDimensionType === InteriorDimensionType.BY_STRING) {
    return addInteriorDimensionsByString(building, levelId, dimExt);
  } else if (interiorDimensionType === InteriorDimensionType.BY_ROOM_GROUP) {
    const { rooms, walls } = building;
    const wallConfig = building.settings.wall;

    const { roomSets, levelRooms, levelRoomsByLevel, memberLevelIds } = isCompositeLevel
      ? collectCompositeInteriorDimensionRoomGroups(building, levelId)
      : {
          roomSets: [...building.wallMasses[levelId].openConnectedRoomSets],
          levelRooms: roomsOnLevel(levelId, building.rooms, building.roomsByLevel),
          levelRoomsByLevel: {},
          memberLevelIds: [levelId],
        };

    const diagonalDimL = 20;
    const compositeWallContext = isCompositeLevel
      ? buildCompositeRoomSetWallIndexContext({
          building,
          memberLevelIds,
          levelRoomsByLevel,
          compositeLevelId: levelId,
        })
      : undefined;

    if (isCompositeLevel && !compositeWallContext) {
      return dims;
    }

    for (const room of levelRooms.filter((x) => x.type === RoomType.PATIO)) {
      roomSets.push([room]);
    }

    const visitedRooms: VisitedRoomsTable = {};
    for (const roomSet of roomSets) {
      const roomSetArea = calculateRoomSetArea(roomSet, rooms, visitedRooms);
      // validate room set
      if (roomSetArea < 0) {
        continue;
      }

      if (roomSetArea / 144 < interiorDimensionAreaThreshold) {
        continue;
      }

      // calculate the aabb of the room set
      let roomSetAABB = aabb2New(
        Number.POSITIVE_INFINITY,
        Number.POSITIVE_INFINITY,
        Number.NEGATIVE_INFINITY,
        Number.NEGATIVE_INFINITY,
      );

      for (const roomLike of roomSet) {
        const { id } = roomLike;
        const { outline } = rooms[id];

        roomSetAABB = aabb2Union(
          roomSetAABB,
          aabb2Grow(polyLine2AABB2(outline), -wallConfig.interiorWallOffset, -wallConfig.interiorWallOffset),
        );
      }

      if (!aabb2IsValid(roomSetAABB)) {
        continue;
      }

      // exterior rooms will be dimensioned on exterior dim string
      const interiorRoomSet = roomSet.filter((r) => isInteriorRoomEnclosure[r.attributes.roomEnclosure]);
      if (!isCompositeLevel) {
        const dimensionBehavior = building.dimensionBehavior[levelId];
        const { wallIndex } = walls[levelId];

        addInteriorDimensionsByRoomGroup({
          roomSet: interiorRoomSet,
          rooms,
          levelRooms,
          wallIndex,
          dims,
          levelId,
          diagonalDimL,
          dimExt,
          dimensionBehavior,
          wallTable: walls,
        });
        continue;
      }

      if (!compositeWallContext) {
        continue;
      }

      addInteriorDimensionsByRoomGroup({
        roomSet: interiorRoomSet,
        rooms,
        levelRooms,
        wallIndex: compositeWallContext.wallIndex,
        dims,
        levelId,
        wallLevelId: compositeWallContext.wallLevelId,
        compositeLevelId: levelId,
        diagonalDimL,
        dimExt,
        dimensionBehavior: compositeWallContext.dimensionBehavior,
        wallTable: compositeWallContext.wallTable,
        resolveHalfEdgeData: compositeWallContext.resolveHalfEdgeData,
        resolveHalfEdgeLine: compositeWallContext.resolveHalfEdgeLine,
      });
    }
  }

  return dims;
}

function addInteriorDimensionsByString(
  building: Pick<Building, "planarGraphs" | "roomsByLevel" | "settings" | "walls" | "dimensionBehavior">,
  levelId: string,
  dimExt: number,
) {
  const BY_STRING_EXPAND_RANGE_AMOUNT = 64;
  const BY_STRING_INITIAL_DIM_LENGTH = -7;
  const { dimStrips, dimStripToHalfEdgeMap } = getInteriorDimensionStripsByString(building, levelId);

  const dims: DimensionGeometryProps[] = [];

  for (const dimStripIndex of Object.keys(dimStrips)) {
    const dimStrip = dimStrips[dimStripIndex];
    const dimStripID = `${levelId}-${dimStripToHalfEdgeMap[+dimStripIndex].flat().join("-")}`;

    for (const dimLineIndex in dimStrip) {
      const dimLine = dimStrip[dimLineIndex];
      const rangeAABB = polyLine2AABB2(dimLine);

      const [addX, addY] = polyLine2EdgeEvalOffset(dimLine, 0, -BY_STRING_EXPAND_RANGE_AMOUNT, 0.5, true);

      const [nx, ny] = line2Normal(dimLine[0], dimLine[1], dimLine[2], dimLine[3]);
      aabb2AddPoint(rangeAABB, addX, addY);
      const [addX2, addY2] = polyLine2EdgeEvalOffset(dimLine, 0, BY_STRING_EXPAND_RANGE_AMOUNT, 0.5, true);
      aabb2AddPoint(rangeAABB, addX2, addY2);
      const baseLine = polyLine2Copy(dimLine);
      polyLine2Move(dimLine, nx * BY_STRING_INITIAL_DIM_LENGTH, ny * BY_STRING_INITIAL_DIM_LENGTH);

      const [dimLabelX, dimLabelY] = getDimLabelPosition(dimLine);

      const halfEdgeData = polyLine2EdgeDataNewFromHalfEdges(
        levelId,
        dimStripToHalfEdgeMap[dimStripIndex]?.[dimLineIndex] ?? [],
      );

      const dim: DimensionGeometryProps = {
        pl: dimLine,
        baseLine,
        dimExt,
        labelPosX: dimLabelX,
        labelPosY: dimLabelY,
        rangeAABB,
        halfEdges: halfEdgeData,
        dimStrip: dimStripID,
      };

      dims.push(dim);
    }
  }

  return dims;
}

function generatedInteriorDimensionHydrateDocEntityHandle(
  dims: DimensionGeometryProps[],
  building: Pick<Building, "mirror" | "planarGraphs" | "roomsByLevel" | "settings" | "rooms">,
  levelId: string,
): GeneratedDimensionMap {
  const planarGraph = building.planarGraphs[levelId];
  const roomIdsOnLevel = building.roomsByLevel[levelId];
  const criteria = makeProvenanceCriteria(building.settings, AutoInteriorDimensionProvenanceCriteria);
  let hydratedInteriorDimensions: GeneratedDimensionMap = {};
  const { interiorDimensionType } = building.settings.planset;

  dims.forEach((dim) => {
    const { halfEdges } = dim;
    const { halfEdgeData } = collectDefinedHalfEdges(halfEdges);

    if (halfEdges?.length) {
      const isDimensionByRoomGroup = interiorDimensionType === InteriorDimensionType.BY_ROOM_GROUP;

      if (isDimensionByRoomGroup) {
        const roomEdges = getRoomEdgesFromHalfEdgeData(
          halfEdgeData,
          building.planarGraphs,
          building.roomsByLevel,
          building?.rooms ?? {},
          false,
        );
        //use the first room edge to generate the handle
        const intDimEntityHandle = autoDimensionFloorPlanInteriorByOrthogonalRoomEdgeHandleNew(
          criteria,
          levelId,
          roomEdges,
        );
        const handleHash = docEntityHandleHash(intDimEntityHandle);
        if (handleHash.length !== 0) {
          hydratedInteriorDimensions = multimapAdd(
            hydratedInteriorDimensions,
            handleHash,
            generatedInteriorDimensionNew({
              ...dim,
              halfEdges,
              docEntityHandle: intDimEntityHandle,
            }),
          );
        }
      } else {
        const filteredHalfEdges = halfEdges.filter(
          (halfEdge) => halfEdge !== undefined && halfEdge?.halfEdge !== undefined,
        );

        if (filteredHalfEdges.length === 0) {
          return;
        }

        const uniqueFilteredHalfEdgesNumbers = edgeDataUnique(filteredHalfEdges);

        const roomEdgePairs: [RoomEdge, RoomEdge][] = [];

        const finalHalfEdgesIncludingOpposite: EdgeData[] = [];

        for (const halfEdgeData of uniqueFilteredHalfEdgesNumbers) {
          if (halfEdgeData?.halfEdge === undefined) {
            continue;
          }

          const halfEdge = halfEdgeData.halfEdge;
          const oppositeHalfEdge = oppositeHalfEdgeIndex(halfEdge);

          const roomEdges = getRoomEdgesFromHalfEdges(
            [halfEdge, oppositeHalfEdge],
            planarGraph,
            roomIdsOnLevel,
            building?.rooms ?? {},
            false,
          );

          if (roomEdges.length !== 2) {
            continue;
          }
          roomEdgePairs.push([roomEdges[0], roomEdges[1]]);
          finalHalfEdgesIncludingOpposite.push(halfEdgeDataNew(levelId, halfEdge));
          finalHalfEdgesIncludingOpposite.push(halfEdgeDataNew(levelId, oppositeHalfEdge));
        }
        const intDimEntityHandle = autoDimensionFloorPlanInteriorByRoomEdgePairHandleNew(
          criteria,
          levelId,
          roomEdgePairs,
        );
        const handleHash = docEntityHandleHash(intDimEntityHandle);
        if (handleHash.length !== 0) {
          hydratedInteriorDimensions = multimapAdd(
            hydratedInteriorDimensions,
            handleHash,
            generatedInteriorDimensionNew({
              ...dim,
              halfEdges: finalHalfEdgesIncludingOpposite,
              docEntityHandle: intDimEntityHandle,
            }),
          );
        }
      }
      return;
    }
  });
  return hydratedInteriorDimensions;
}
